import type { AutomationDefinition } from "@redotech/redo-automations-model/graph/automation";
import type {
  JsonObject,
  JsonValue,
} from "@redotech/redo-automations-model/json";
import { createContext } from "react";

/**
 * What the screens need from the server, in the shape of the `/automations-rpc`
 * endpoints. Screens depend on this, not on the RPC client, so they can be
 * built and exercised before the server exists.
 */
export type AutomationStatus = "draft" | "active" | "inactive";

/**
 * Which version a waiting run continues on: "pinned" (the default) finishes
 * it on the version it started on, "latest" moves it to the newest published
 * version.
 */
export type VersionStrategy = "latest" | "pinned";

export interface AutomationDiagnostic {
  severity: "error" | "warning";
  code: string;
  message: string;
  nodeId?: string;
  /** The config field or binding a step's issue is about, e.g. "inputQuery". */
  bindingKey?: string;
}

export interface AutomationSummary {
  id: string;
  name: string;
  triggerKind: string;
  status: AutomationStatus;
  publishedVersion: number | null;
  versionStrategy: VersionStrategy;
  updatedAt: Date;
}

export interface AutomationDetail extends AutomationSummary {
  draft: AutomationDefinition;
  /** Sent back with the next save or publish; the server refuses either if the draft changed since. */
  draftRevision: string;
}

export interface DraftResult {
  automation: AutomationDetail;
  diagnostics: AutomationDiagnostic[];
}

export interface CatalogTrigger {
  id: string;
  version: number;
  domainTitle: string;
  group: string;
  title: string;
  description: string;
  unavailableReason: string | null;
  exampleEvent: JsonValue;
}

export interface CatalogStep {
  id: string;
  category: "action" | "condition";
  group: string;
  title: string;
  description: string;
  hidden: string | null;
  availability: { ok: boolean; reason: string | null } | null;
}

export interface Catalog {
  triggers: CatalogTrigger[];
  steps: CatalogStep[];
}

export type EffectResult =
  | { ok: true; value: JsonValue }
  | { ok: false; error: { message: string } };

export interface StepRecord {
  nodeId: string;
  kind: string;
  startedAt: string;
  outcome?: "completed" | "disabled" | "failed" | "cancelled";
  port?: string;
  inputs?: Record<string, JsonValue>;
  output?: JsonValue;
  note?: string;
  effects: { type: string; input: JsonValue; result: EffectResult }[];
}

export type RunStatus =
  | "running"
  | "completed"
  | "failed"
  | "rejected"
  | "cancelled";

export interface ScheduledEvent {
  eventType?: string;
  nodeId?: string;
  afterSeconds: number;
  payload: JsonValue;
}

export interface Scenario {
  payload?: JsonValue;
  resolvers?: Record<string, { value?: JsonValue; fail?: boolean }>;
  events?: ScheduledEvent[];
  records?: Record<string, JsonObject>;
  random?: Record<string, number>;
}

export type SimulateResult =
  | {
      ok: true;
      status: RunStatus;
      path: string[];
      steps: StepRecord[];
      suspendedOn: string | null;
      failure: { nodeId: string | null; message: string } | null;
      rejection: string | null;
      /** What each Function step's code logged, by step id. */
      logs: Record<string, string>;
    }
  | { ok: false; diagnostics: AutomationDiagnostic[] };

export interface ExploredPath {
  id: string;
  steps: { nodeId: string; port: string; label: string; how: string }[];
  end: string;
  status: "proven" | "infeasible" | "unknown";
  witness: JsonValue;
  note: string | null;
}

export type ExploreResult =
  | {
      ok: true;
      paths: ExploredPath[];
      truncated: boolean;
      unreachable: string[];
    }
  | { ok: false; diagnostics: AutomationDiagnostic[] };

export type PublishResult =
  | { ok: true; automation: AutomationSummary; version: number }
  | { ok: false; message: string; diagnostics: AutomationDiagnostic[] };

/** What publishing the draft would do to the runs in progress. */
export interface PublishPreview {
  /** Runs that would continue on the new version. */
  moving: number;
  /** Runs that would be cancelled: their step was removed or changed kind. */
  ending: number;
  /** Runs that move or are cancelled at their next wait, depending on the step they're on then. */
  atNextWait: number;
  /** Pinned runs, which finish on the version they're on. */
  staying: { version: number; count: number }[];
  /** More runs are in progress than were counted (`limit`). */
  truncated: boolean;
  limit: number;
}

export interface VersionSummary {
  version: number;
  createdBy: string | null;
  createdAt: Date;
}

export interface RunSummary {
  id: string;
  automationId: string;
  version: number;
  status: string;
  waitEvent: string | null;
  startedAt: Date;
  finishedAt: Date | null;
}

export interface RunDetail extends RunSummary {
  steps: StepRecord[];
  failure: { nodeId: string | null; message: string } | null;
  rejection: string | null;
}

export type RunNowOutcome =
  | "invalid"
  | "empty"
  | "filtered"
  | "rejected"
  | "finished"
  | "started"
  | "existing";

export type RunNowResult =
  | {
      ok: true;
      requestId: string;
      /** Null when the trigger's filter dropped the event. */
      runId: string | null;
      outcome: RunNowOutcome;
      reason: string | null;
      status: string | null;
    }
  | { ok: false; diagnostics: AutomationDiagnostic[] };

export interface AutomationsApi {
  listAutomations(): Promise<AutomationSummary[]>;
  getAutomation(id: string): Promise<AutomationDetail>;
  /** A new draft; without a trigger, the editor starts by choosing one. */
  createAutomation(input: {
    triggerKind?: string;
    name?: string;
  }): Promise<DraftResult>;
  saveDraft(
    id: string,
    draft: AutomationDefinition,
    draftRevision: string,
  ): Promise<DraftResult>;
  previewPublish(id: string, draftRevision: string): Promise<PublishPreview>;
  publish(id: string, draftRevision: string): Promise<PublishResult>;
  setActive(id: string, active: boolean): Promise<AutomationSummary>;
  setVersionStrategy(
    id: string,
    strategy: VersionStrategy,
  ): Promise<AutomationSummary>;
  deleteAutomation(id: string): Promise<void>;
  listVersions(id: string): Promise<VersionSummary[]>;
  /** A published version's definition, e.g. the one a run ran on. */
  getVersion(id: string, version: number): Promise<AutomationDefinition>;
  /** Copies a published version into the draft. */
  restoreVersion(
    id: string,
    version: number,
    draftRevision: string,
  ): Promise<DraftResult>;
  validate(definition: AutomationDefinition): Promise<AutomationDiagnostic[]>;
  /** `scenario` is a Scenario, or a path's witness passed through as stored. */
  simulate(
    definition: AutomationDefinition,
    scenario: JsonValue,
  ): Promise<SimulateResult>;
  explore(definition: AutomationDefinition): Promise<ExploreResult>;
  catalog(triggerKind?: string): Promise<Catalog>;
  /** Runs the published version once with `payload` as the trigger event. */
  runNow(input: {
    automationId: string;
    payload: JsonValue;
    requestId?: string;
  }): Promise<RunNowResult>;
  listRuns(id: string): Promise<RunSummary[]>;
  getRun(runId: string): Promise<RunDetail>;
}

export const AutomationsApiContext = createContext<AutomationsApi | undefined>(
  undefined,
);
AutomationsApiContext.displayName = "AutomationsApiContext";
